import React, { useRef } from "react";
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Spinner,
} from "@chakra-ui/react";

// The "are you sure?" step in front of Trigger Auto-Reminders and Trigger
// Auto-Confirmations. Both buttons used to fire immediately; now the click
// asks the backend for a dry run and this dialog says what will happen —
// when, for how many people, under which filters — before Proceed does it.
//
// `preview` is the shape built by the two `build*Preview` helpers below:
//   { facts: [{label, value}], willDo: [{name, detail}], wontDo: [{name, reason}],
//     counts: {do, skip}, warning?: string }

/** Preview of Trigger Auto-Reminders from the poll's dry-run response. */
export const buildReminderPreview = (data, { schema, date }) => {
  const eff = data?.effective_settings || {};
  const scheduled = data?.scheduled_patients || [];
  const skipped = Object.entries(data?.skipped_reasons || {}).map(([name, v]) => ({
    name,
    reason: typeof v === "string" ? v : v?.reason || "",
  }));
  const methods = [eff.include_phone && "SMS", eff.include_email && "email"].filter(Boolean);
  const days = eff.days_before;
  const facts = [
    { label: "Clinic", value: schema || "public" },
    { label: "Appointments", value: eff.scope === "all upcoming appointments" ? "every upcoming appointment (today onward)" : `on ${eff.scope || date}` },
    { label: "Send time", value: days == null || !eff.time ? "not configured" : `${days} day${days === 1 ? "" : "s"} before each appointment at ${String(eff.time).slice(0, 5)} (${eff.timezone || "clinic time"})` },
    { label: "Contact", value: methods.length ? methods.join(" + ") : "none selected" },
    { label: "Patient types", value: (eff.patient_types || []).length ? eff.patient_types.join(", ") : "NP, NS" },
    { label: "Skips", value: "patients who already have a reminder sent or scheduled, or no phone/email" },
  ];
  return {
    facts,
    willDo: scheduled.map((p) => ({
      name: p.name,
      detail: `${p.appointment_date || ""}${p.appointment_time ? ` ${String(p.appointment_time).slice(0, 5)}` : ""} · ${p.contact_method || ""} · sends ${p.scheduled_for || ""}`,
    })),
    wontDo: skipped,
    counts: { do: data?.reminders_scheduled || 0, skip: data?.skipped || 0 },
    warning: data?.message && /not configured|disabled/i.test(data.message) ? data.message : null,
  };
};

const ruleDays = (rule) => {
  const h = Number(rule?.hours_before);
  if (!Number.isFinite(h) || h <= 0) return rule?.id || "?";
  const d = Math.round((h / 24) * 100) / 100;
  return `${d} day${d === 1 ? "" : "s"} before`;
};

/** Preview of Trigger Auto-Confirmations from the pass's dry-run response. */
export const buildConfirmationPreview = (data, { schema }) => {
  const settings = data?.settings || {};
  const rules = settings.rules || [];
  const would = data?.would_schedule || [];
  const cancel = data?.would_cancel || [];
  const patients = new Set(would.map((w) => w.notes_c_id)).size;
  const facts = [
    { label: "Clinic", value: schema || "public" },
    { label: "Appointments", value: `every open appointment (not yet C or R) with a phone number, today through the next ${data?.horizon_days ?? 60} days` },
    { label: "Send time", value: rules.length ? `${rules.map(ruleDays).join(" and ")} — at each appointment's own time (${data?.timezone || "clinic time"})` : "no rules configured" },
    { label: "Contact", value: "SMS" },
    { label: "Patient types", value: (settings.patient_types || []).length ? settings.patient_types.join(", ") : "all types" },
    { label: "Automatic sending", value: settings.enabled ? "on" : "OFF — nothing will be queued until it is turned on in Appointment confirmations" },
  ];
  return {
    facts,
    willDo: would.map((w) => ({
      name: w.patient_name || w.notes_c_id,
      detail: `${w.appointment_date || ""}${w.appointment_time ? ` ${w.appointment_time}` : ""} · ${ruleDays({ hours_before: w.hours_before, id: w.confirmation_rule })} · sends ${w.scheduled_for ? new Date(w.scheduled_for).toLocaleString() : ""}`,
    })),
    wontDo: cancel.map((c) => ({ name: c.patient_name || c.notes_c_id, reason: `queued text will be cancelled — ${c.reason}` })),
    counts: { do: data?.scheduled || 0, skip: data?.cancelled || 0, patients },
    warning: settings.enabled ? null : "Automatic confirmations are turned off for this clinic, so this run will only cancel stale queued texts.",
  };
};

const List = ({ items, empty, render, testId }) => (
  <Box as="ul" pl={4} maxHeight="140px" overflowY="auto" fontSize="sm" data-testid={testId}>
    {items.length === 0 ? (
      <Text as="li" listStyleType="none" color="gray.500" fontStyle="italic">{empty}</Text>
    ) : (
      items.map((item, i) => <Text as="li" key={`${item.name}-${i}`}>{render(item)}</Text>)
    )}
  </Box>
);

const TriggerPreviewDialog = ({
  isOpen,
  onClose,
  title,
  loading,
  error,
  preview,
  doLabel = "Will be scheduled",
  skipLabel = "Will be skipped",
  proceedLabel = "Proceed",
  isProceeding,
  onProceed,
}) => {
  const cancelRef = useRef(null);
  const counts = preview?.counts || { do: 0, skip: 0 };
  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={() => !isProceeding && onClose()} isCentered size="lg">
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">{title}</AlertDialogHeader>
          <AlertDialogBody>
            {loading ? (
              <HStack data-testid="trigger-preview-loading">
                <Spinner size="sm" />
                <Text fontSize="sm">Working out what this run would do…</Text>
              </HStack>
            ) : error ? (
              <Text color="red.600" fontSize="sm" data-testid="trigger-preview-error">{error}</Text>
            ) : preview ? (
              <VStack align="stretch" spacing={3}>
                {preview.warning && (
                  <Text fontSize="sm" color="orange.700" fontWeight="semibold" data-testid="trigger-preview-warning">
                    {preview.warning}
                  </Text>
                )}
                <Box as="dl" fontSize="sm" data-testid="trigger-preview-facts">
                  {preview.facts.map((f) => (
                    <HStack key={f.label} align="flex-start" spacing={2}>
                      <Text as="dt" fontWeight="semibold" minW="120px">{f.label}:</Text>
                      <Text as="dd">{f.value}</Text>
                    </HStack>
                  ))}
                </Box>
                <HStack spacing={3} data-testid="trigger-preview-counts">
                  <Badge colorScheme="green" fontSize="sm" px={2}>{counts.do} {doLabel.toLowerCase()}</Badge>
                  {counts.patients != null && (
                    <Text fontSize="sm" color="gray.600">for {counts.patients} patient{counts.patients === 1 ? "" : "s"}</Text>
                  )}
                  <Badge colorScheme="orange" fontSize="sm" px={2}>{counts.skip} {skipLabel.toLowerCase()}</Badge>
                </HStack>
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={1}>{doLabel}</Text>
                  <List items={preview.willDo} empty="Nothing — every eligible patient is already covered." testId="trigger-preview-will" render={(p) => `${p.name} — ${p.detail}`} />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={1}>{skipLabel}</Text>
                  <List items={preview.wontDo} empty="Nothing." testId="trigger-preview-wont" render={(p) => `${p.name} — ${p.reason}`} />
                </Box>
                <Text fontSize="xs" color="gray.500">
                  Nothing has been written yet. Proceed runs it for real.
                </Text>
              </VStack>
            ) : null}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isProceeding}>Cancel</Button>
            <Button
              colorScheme="purple"
              ml={3}
              onClick={onProceed}
              isLoading={isProceeding}
              loadingText="Running..."
              isDisabled={loading || Boolean(error) || !preview}
              data-testid="trigger-preview-proceed"
            >
              {proceedLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default TriggerPreviewDialog;
